import { INVOICE_TYPE } from '~~/constants/invoices';
import { ORDER_STATUS } from '~~/constants/orderStatus';
import { MILISECONDS_IN_DAY } from '~~/constants/timeUnits';
import { roundToTwoDecimals } from '~~/helpers/roundToTwoDecimals/roundToTwoDecimals';
import type {
  OrderAddress,
  OrderHistoryEntry,
  OrderHistoryLineItem,
} from '~~/types/myaccount/OrderHistory';

/**
 * Fake order history for the My orders page, so states nobody has a real order for - entirely
 * refunded, partially refunded, cancelled - can still be looked at while working on the UI.
 *
 * Every order is billed in one fixed currency at one fixed VAT rate rather than in the visitor's,
 * because that is what lets the amounts be precomputed and still add up
 * (totalAmount = subtotal + shippingCost + taxAmount, the sum the order detail view prints).
 * The product prices are the real gross prices from the catalogue, so they are only exactly right
 * for a 20% VAT market - close enough everywhere else for a mock whose point is the order states.
 *
 * The SKUs are real, so the page's commercetools lookup resolves actual product images, product
 * links and manuals for these line items - only the orders wrapping them are invented.
 */

const MOCK_CURRENCY = 'EUR';

const VAT_RATE = 0.2;

interface MockProduct {
  sku: string;
  name: string;
  /** Gross list price, as shown struck through on the product card. */
  regular: number;
  /** Gross price actually charged. Equal to `regular` when the product is not on sale. */
  final: number;
}

const MOCK_PRODUCTS = {
  POPCORN_MACHINE_WITH_CART: {
    sku: '10010537',
    name: 'Popcorn Machine with Cart - retro design - red',
    regular: 396,
    final: 350,
  },
  SHOE_POLISHING_MACHINE: {
    sku: '10080021',
    name: 'Shoe polishing machine - 3 brushes',
    regular: 104,
    final: 99,
  },
  SUITCASE_STAND: {
    sku: '10010379',
    name: 'Suitcase Stand - folding - over 50 kg',
    regular: 45,
    final: 45,
  },
  HYDRAULIC_WORKSHOP_PRESS: {
    sku: '10060301',
    name: 'Hydraulic Workshop Press - Up to 30 Tons',
    regular: 625,
    final: 622,
  },
  PORTABLE_SANDBLASTER: {
    sku: '10060306',
    name: 'Portable Sandblaster - 76 l - 9 bar',
    regular: 162,
    final: 161,
  },
  CONTACT_GRILL: {
    sku: '10010330',
    name: 'Contact Grill - 1 x 1800 Watt',
    regular: 123,
    final: 122,
  },
  WHEEL_DOLLY: {
    sku: '10060931',
    name: 'Wheel Dolly - 680 kg each - 2 pcs',
    regular: 70,
    final: 69,
  },
  GARDEN_COMPOST_BIN: {
    sku: '10090211',
    name: 'Garden Compost Bin - 860 L',
    regular: 104,
    final: 103,
  },
  CHAIR_SET_OF_FOUR: {
    sku: '10260139',
    name: 'Chair - set of 4 - up to 150 kg - seat 52 x 46.5 cm - black',
    regular: 142,
    final: 135,
  },
  SOFT_ICE_MACHINE: {
    sku: '10011812',
    name: 'Soft Ice Machine - 200 W - 1.5 L',
    regular: 355,
    final: 352,
  },
  DUAL_POWER_SUPPLY: {
    sku: '10021058',
    name: 'Dual Power Supply - 2x 0-30 V, 0-5 A DC, 550 W',
    regular: 206,
    final: 205,
  },
  WELL_PUMP: {
    sku: '10060171',
    name: 'Well Pump - 6 m³/h - 370 W - Stainless Steel',
    regular: 153,
    final: 124,
  },
  SALAMANDER_GRILL: {
    sku: '10010247',
    name: 'Salamander Grill - 2000 Watt',
    regular: 143,
    final: 140,
  },
  ADJUSTABLE_PANEL_STAND: {
    sku: '10060915',
    name: 'Adjustable Panel Stand - 220 kg',
    regular: 49,
    final: 46,
  },
  ROLLATOR_WHEELCHAIR: {
    sku: '10250028',
    name: 'Rollator Wheelchair 2-in-1 - silver - 120 kg',
    regular: 162,
    final: 161,
  },
  FOOD_SLICER: {
    sku: '10010171',
    name: 'Food Slicer - 250 mm - up to 12 mm',
    regular: 307,
    final: 305,
  },
} as const satisfies Record<string, MockProduct>;

const PRIVATE_ADDRESS: OrderAddress = {
  fullName: 'James Whitfield',
  company: null,
  addressLines: 'Flat 3, 14 Rosebery Avenue',
  city: 'London',
  region: null,
  postalCode: 'EC1R 4TD',
  country: 'United Kingdom',
  countryCode: 'GB',
  phoneNumber: '+44 20 7946 0812',
  email: 'james.whitfield@example.co.uk',
};

const COMPANY_ADDRESS: OrderAddress = {
  fullName: 'James Whitfield',
  company: 'Whitfield Catering Ltd',
  addressLines: 'Unit 7, Bourne Industrial Estate, Crayford Road',
  city: 'Dartford',
  region: 'Kent',
  postalCode: 'DA1 4BF',
  country: 'United Kingdom',
  countryCode: 'GB',
  phoneNumber: '+44 1322 447 190',
  email: 'accounts@whitfield-catering.example.co.uk',
};

const buildLineItem = (
  id: number,
  { product, quantity }: { product: MockProduct; quantity: number },
): OrderHistoryLineItem => {

  return {
    id,
    name: product.name,
    sku: product.sku,
    quantity,
    isDiscounted: product.final < product.regular,
    unitPrice: {
      regular: product.regular,
      final: product.final,
    },
    totalPrice: {
      regular: roundToTwoDecimals(product.regular * quantity),
      final: roundToTwoDecimals(product.final * quantity),
    },
  };

};

/**
 * Line item prices are gross (that is what the MyOrders API returns as `costIncTax`), while
 * `subtotal` and `shippingCost` are the net amounts the order detail view adds `taxAmount` to.
 */
const buildTotals = (lineItems: OrderHistoryLineItem[], grossShippingCost: number) => {

  const grossItemsTotal = roundToTwoDecimals(
    lineItems.reduce((total, lineItem) => total + lineItem.totalPrice.final, 0),
  );
  const totalAmount = roundToTwoDecimals(grossItemsTotal + grossShippingCost);
  const subtotal = roundToTwoDecimals(grossItemsTotal / (1 + VAT_RATE));
  const shippingCost = roundToTwoDecimals(grossShippingCost / (1 + VAT_RATE));

  return {
    currency: MOCK_CURRENCY,
    subtotal,
    shippingCost,
    taxAmount: roundToTwoDecimals(totalAmount - subtotal - shippingCost),
    totalAmount,
  };

};

/**
 * The instant every date in the set is measured back from. Anchoring to a constant rather than to
 * `Date.now()` keeps the mock byte-for-byte identical on every render, between server and client and
 * across environments, so it can be compared and snapshotted.
 *
 * The trade-off is that the set ages: `OrderCancellation` measures its 30-day window against the
 * live clock, so the "purchased" order stops offering its Cancel order button once this date is more
 * than a month old. Move this constant forward when that button needs to be worked on again.
 */
export const MOCK_ORDER_HISTORY_REFERENCE_DATE = new Date('2026-09-01T09:00:00.000Z').getTime();

const timestampDaysBefore = (days: number) => new Date(
  MOCK_ORDER_HISTORY_REFERENCE_DATE - days * MILISECONDS_IN_DAY,
).toISOString();

const dateDaysBefore = (days: number) => timestampDaysBefore(days).slice(0, 10);

export const getMockOrderHistory = (): OrderHistoryEntry[] => {

  const purchasedLineItems = [
    buildLineItem(8810431, { product: MOCK_PRODUCTS.POPCORN_MACHINE_WITH_CART, quantity: 1 }),
    buildLineItem(8810432, { product: MOCK_PRODUCTS.SUITCASE_STAND, quantity: 2 }),
    buildLineItem(8810433, { product: MOCK_PRODUCTS.FOOD_SLICER, quantity: 1 }),
  ];
  const inDeliveryLineItems = [
    buildLineItem(8798104, { product: MOCK_PRODUCTS.CONTACT_GRILL, quantity: 3 }),
    buildLineItem(8798105, { product: MOCK_PRODUCTS.SALAMANDER_GRILL, quantity: 1 }),
    buildLineItem(8798106, { product: MOCK_PRODUCTS.DUAL_POWER_SUPPLY, quantity: 1 }),
  ];
  const deliveredLineItems = [
    buildLineItem(8752663, { product: MOCK_PRODUCTS.HYDRAULIC_WORKSHOP_PRESS, quantity: 1 }),
    buildLineItem(8752664, { product: MOCK_PRODUCTS.WHEEL_DOLLY, quantity: 2 }),
    buildLineItem(8752665, { product: MOCK_PRODUCTS.ADJUSTABLE_PANEL_STAND, quantity: 4 }),
  ];
  const cancelledLineItems = [
    buildLineItem(8771920, { product: MOCK_PRODUCTS.SOFT_ICE_MACHINE, quantity: 1 }),
  ];
  const partiallyRefundedLineItems = [
    buildLineItem(8690337, { product: MOCK_PRODUCTS.CHAIR_SET_OF_FOUR, quantity: 6 }),
    buildLineItem(8690338, { product: MOCK_PRODUCTS.GARDEN_COMPOST_BIN, quantity: 1 }),
    buildLineItem(8690339, { product: MOCK_PRODUCTS.SHOE_POLISHING_MACHINE, quantity: 1 }),
  ];
  const fullyRefundedLineItems = [
    buildLineItem(8611578, { product: MOCK_PRODUCTS.WELL_PUMP, quantity: 2 }),
    buildLineItem(8611579, { product: MOCK_PRODUCTS.PORTABLE_SANDBLASTER, quantity: 1 }),
    buildLineItem(8611580, { product: MOCK_PRODUCTS.ROLLATOR_WHEELCHAIR, quantity: 1 }),
  ];

  const partiallyRefundedTotals = buildTotals(partiallyRefundedLineItems, 0);
  const fullyRefundedTotals = buildTotals(fullyRefundedLineItems, 0);

  /** The two returned sets of chairs, which is what the partial refund covers. */
  const partialRefundAmount = roundToTwoDecimals(MOCK_PRODUCTS.CHAIR_SET_OF_FOUR.final * 2);

  return [
    {
      // "Purchased" - paid for, warehouse has not picked it yet, so it can still be cancelled.
      status: ORDER_STATUS.PAID_IN_PROGRESS,
      orderDate: timestampDaysBefore(2),
      orderNumber: 'SH_10248317',
      lwOrderId: 1248317,
      paymentMethod: 'PayPal',
      paidDate: timestampDaysBefore(2),
      deliveryDate: null,
      cancellationDate: null,
      invoices: [
        {
          id: 6104,
          type: INVOICE_TYPE.VAT,
          invoiceNumber: 'GB-2481037',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/6104',
          issueDate: dateDaysBefore(2),
        },
      ],
      newestMainInvoice: {
        id: 6104,
        type: INVOICE_TYPE.VAT,
        invoiceNumber: 'GB-2481037',
        invoicePdfUrl: 'https://invoices-uat.expondo.com/download/6104',
        issueDate: dateDaysBefore(2),
      },
      shippingAddress: PRIVATE_ADDRESS,
      billingAddress: PRIVATE_ADDRESS,
      shipments: [],
      refunds: [],
      lineItems: purchasedLineItems,
      ...buildTotals(purchasedLineItems, 0),
    },
    {
      // "In delivery" - handed over to the carrier, tracking available, not delivered yet.
      status: ORDER_STATUS.SHIPPED,
      orderDate: timestampDaysBefore(6),
      orderNumber: 'SH_10243982',
      lwOrderId: 1243982,
      paymentMethod: 'Bank transfer',
      paidDate: timestampDaysBefore(5),
      deliveryDate: null,
      cancellationDate: null,
      invoices: [
        {
          id: 6088,
          type: INVOICE_TYPE.VAT,
          invoiceNumber: 'GB-2478551',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/6088',
          issueDate: dateDaysBefore(5),
        },
      ],
      newestMainInvoice: {
        id: 6088,
        type: INVOICE_TYPE.VAT,
        invoiceNumber: 'GB-2478551',
        invoicePdfUrl: 'https://invoices-uat.expondo.com/download/6088',
        issueDate: dateDaysBefore(5),
      },
      shippingAddress: COMPANY_ADDRESS,
      billingAddress: COMPANY_ADDRESS,
      shipments: [
        {
          trackingUrl: 'https://tracking.dpd.de/status/en_GB/parcel/09384756102394',
          trackingNumber: '09384756102394',
          carrier: 'DPD',
        },
      ],
      refunds: [],
      lineItems: inDeliveryLineItems,
      ...buildTotals(inDeliveryLineItems, 0),
    },
    {
      // "Cancelled" - cancelled before payment cleared, so it never got past a proforma invoice.
      status: ORDER_STATUS.CANCELLED,
      orderDate: timestampDaysBefore(12),
      orderNumber: 'SH_10236640',
      lwOrderId: 1236640,
      paymentMethod: 'Bank transfer',
      paidDate: null,
      deliveryDate: null,
      cancellationDate: timestampDaysBefore(10),
      invoices: [
        {
          id: 6031,
          type: INVOICE_TYPE.PROFORMA,
          invoiceNumber: 'GB-PRO-2472094',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/6031',
          issueDate: dateDaysBefore(12),
        },
      ],
      shippingAddress: PRIVATE_ADDRESS,
      billingAddress: PRIVATE_ADDRESS,
      shipments: [],
      refunds: [],
      lineItems: cancelledLineItems,
      ...buildTotals(cancelledLineItems, 0),
    },
    {
      // "Delivered" - the happy path, and the only set here that pays for shipping.
      status: ORDER_STATUS.DELIVERED,
      orderDate: timestampDaysBefore(24),
      orderNumber: 'SH_10219475',
      lwOrderId: 1219475,
      paymentMethod: 'Credit card',
      paidDate: timestampDaysBefore(24),
      deliveryDate: timestampDaysBefore(18),
      cancellationDate: null,
      invoices: [
        {
          id: 5974,
          type: INVOICE_TYPE.VAT,
          invoiceNumber: 'GB-2463118',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5974',
          issueDate: dateDaysBefore(23),
        },
      ],
      newestMainInvoice: {
        id: 5974,
        type: INVOICE_TYPE.VAT,
        invoiceNumber: 'GB-2463118',
        invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5974',
        issueDate: dateDaysBefore(23),
      },
      shippingAddress: COMPANY_ADDRESS,
      billingAddress: COMPANY_ADDRESS,
      shipments: [
        {
          trackingUrl: 'https://gls-group.eu/GB/en/parcel-tracking?match=51873094662',
          trackingNumber: '51873094662',
          carrier: 'GLS',
        },
      ],
      refunds: [],
      lineItems: deliveredLineItems,
      ...buildTotals(deliveredLineItems, 49.9),
    },
    {
      // "Refunded partially" - two of the six chair sets went back, the compost bin claim was
      // declined, so this one also covers a refund that did not go through.
      status: ORDER_STATUS.PARTIALLY_RETURNED,
      orderDate: timestampDaysBefore(48),
      orderNumber: 'SH_10184206',
      lwOrderId: 1184206,
      paymentMethod: 'Credit card',
      paidDate: timestampDaysBefore(48),
      deliveryDate: timestampDaysBefore(41),
      cancellationDate: null,
      invoices: [
        {
          id: 5712,
          type: INVOICE_TYPE.VAT,
          invoiceNumber: 'GB-2431885',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5712',
          issueDate: dateDaysBefore(47),
        },
        {
          id: 5860,
          type: INVOICE_TYPE.CORRECTION,
          invoiceNumber: 'GB-COR-2431885',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5860',
          issueDate: dateDaysBefore(29),
        },
      ],
      newestMainInvoice: {
        id: 5712,
        type: INVOICE_TYPE.VAT,
        invoiceNumber: 'GB-2431885',
        invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5712',
        issueDate: dateDaysBefore(47),
      },
      shippingAddress: COMPANY_ADDRESS,
      billingAddress: COMPANY_ADDRESS,
      shipments: [
        {
          trackingUrl: 'https://www.dhl.com/gb-en/home/tracking.html?tracking-id=JD0002080113649275',
          trackingNumber: 'JD0002080113649275',
          carrier: 'DHL',
        },
      ],
      refunds: [
        {
          amount: partialRefundAmount,
          completedAt: timestampDaysBefore(29),
          draftCreatedAt: timestampDaysBefore(33),
          declinedAt: null,
        },
        {
          amount: MOCK_PRODUCTS.GARDEN_COMPOST_BIN.final,
          completedAt: null,
          draftCreatedAt: timestampDaysBefore(21),
          declinedAt: timestampDaysBefore(19),
        },
      ],
      lineItems: partiallyRefundedLineItems,
      ...partiallyRefundedTotals,
    },
    {
      // "Refunded entirely" - the whole order went back and the full charge was returned.
      status: ORDER_STATUS.RETURNED,
      orderDate: timestampDaysBefore(75),
      orderNumber: 'SH_10152874',
      lwOrderId: 1152874,
      paymentMethod: 'Cash on delivery',
      paidDate: timestampDaysBefore(73),
      deliveryDate: timestampDaysBefore(68),
      cancellationDate: null,
      invoices: [
        {
          id: 5488,
          type: INVOICE_TYPE.VAT,
          invoiceNumber: 'GB-2405613',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5488',
          issueDate: dateDaysBefore(74),
        },
        {
          id: 5601,
          type: INVOICE_TYPE.CORRECTION,
          invoiceNumber: 'GB-COR-2405613',
          invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5601',
          issueDate: dateDaysBefore(59),
        },
      ],
      newestMainInvoice: {
        id: 5488,
        type: INVOICE_TYPE.VAT,
        invoiceNumber: 'GB-2405613',
        invoicePdfUrl: 'https://invoices-uat.expondo.com/download/5488',
        issueDate: dateDaysBefore(74),
      },
      shippingAddress: PRIVATE_ADDRESS,
      billingAddress: PRIVATE_ADDRESS,
      shipments: [
        {
          trackingUrl: 'https://tracking.dpd.de/status/en_GB/parcel/09384712045518',
          trackingNumber: '09384712045518',
          carrier: 'DPD',
        },
      ],
      refunds: [
        {
          amount: fullyRefundedTotals.totalAmount,
          completedAt: timestampDaysBefore(59),
          draftCreatedAt: timestampDaysBefore(63),
          declinedAt: null,
        },
      ],
      lineItems: fullyRefundedLineItems,
      ...fullyRefundedTotals,
    },
  ];

};
